import { MessageCircleQuestion, MessageSquare, Mic } from 'lucide-react'
import type { ReactNode } from 'react'
import { CtaBanner } from '../components/cta-banner'
import { EvaluationActions } from './components/evaluation-actions'
import { EvaluationDashboardLink } from './components/evaluation-dashboard-link'
import {
  EvaluationFeedback,
  type EvaluationFeedbackData,
} from './components/evaluation-feedback.js'
import { EvaluationHeader } from './components/evaluation-header'
import { EvaluationImprovements } from './components/evaluation-improvements'
import { EvaluationInvestmentThesis } from './components/evaluation-investment-thesis'
import { EvaluationRisks } from './components/evaluation-risks'
import { EvaluationSummary } from './components/evaluation-summary'
import { EvaluationTensions } from './components/evaluation-tensions'
import { JudgeCard } from './components/judge-card'
import { MetricBreakdownCard } from './components/metric-breakdown-card'
import { MetricScoresPanel } from './components/metric-scores-panel'
import { OnlyYouNote } from './components/only-you-note'
import type {
  CoreTension,
  EvalAction,
  EvalImprovement,
  EvalJudgeCard,
  EvalMetric,
  MetricBreakdown,
  RiskSeverity,
  WinnerJudge,
} from './types'

export interface PitchEvaluationViewProps {
  title: string
  sessionLabel: string
  date: string
  pitchDuration: string
  summary: string
  score: number | null
  metrics: EvalMetric[]
  winnerJudge: WinnerJudge | null
  judges: EvalJudgeCard[]
  metricBreakdowns: MetricBreakdown[]
  tensions: CoreTension[]
  risks: {
    label: string
    severity: RiskSeverity
    detail: string
  }[]
  actions: EvalAction[]
  openQuestions?: { title: string; detail: string }[]
  improvements: EvalImprovement[]
  judgeDetailPath: (judgeKey: string) => string
  homePath: string
  pitchPath: string
  isPrescreen?: boolean
  /** 'founder' shows full view, 'investor' hides founder-specific sections and adds connect CTA */
  variant?: 'founder' | 'investor'
  /** Called when investor clicks "Connect with startup" */
  onConnect?: () => void
  onFeedbackSubmit?: (data: EvaluationFeedbackData) => void
  /** Called when user clicks play recording — passes current preview time */
  onPlayRecording?: (currentTime?: number) => void
  /** Called when user clicks Pitch Deck button */
  onOpenPitchDeck?: () => void
  /** Video URL for inline preview */
  videoUrl?: string | null
  /** Whether the video modal is currently open */
  videoModalOpen?: boolean
  /** Time to resume preview at after modal closes */
  videoResumeTime?: number
  /** Optional right-aligned action slot rendered in the header */
  headerAction?: ReactNode
  /** Optional investor info card rendered between header and dashboard link — founder only */
  investorCard?: ReactNode
}

export function PitchEvaluationView({
  title,
  sessionLabel,
  date,
  pitchDuration,
  summary,
  score,
  metrics,
  winnerJudge,
  judges,
  metricBreakdowns,
  tensions,
  risks,
  actions,
  openQuestions,
  improvements,
  judgeDetailPath,
  homePath,
  pitchPath,
  isPrescreen,
  variant = 'founder',
  onConnect,
  onFeedbackSubmit,
  onPlayRecording,
  onOpenPitchDeck,
  videoUrl,
  videoModalOpen,
  videoResumeTime,
  headerAction,
  investorCard,
}: PitchEvaluationViewProps) {
  const isInvestor = variant === 'investor'
  const hasOpenQuestions = !!openQuestions && openQuestions.length > 0
  const isSingleJudge = judges.length === 1
  const inlineOpenQuestions = isSingleJudge && hasOpenQuestions

  const openQuestionsPanel = hasOpenQuestions ? (
    <div className="h-full rounded-xl border border-border bg-card/30 px-6 py-5">
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/80">
        What Investors Will Ask
      </h2>
      <div className="space-y-3">
        {openQuestions?.map((q) => (
          <div key={q.title} className="flex items-start gap-3">
            <MessageCircleQuestion className="mt-0.5 size-4 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-foreground">{q.title}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground/70">{q.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : null

  return (
    <div className="mx-auto max-w-6xl py-2">
      {/* Header */}
      <EvaluationHeader
        title={title}
        sessionLabel={sessionLabel}
        date={date}
        pitchDuration={pitchDuration}
        score={score}
        action={headerAction}
      />

      {!isInvestor && (
        <div className="mt-4">
          <OnlyYouNote />
        </div>
      )}

      {/* Investor info card — founder only */}
      {!isInvestor && investorCard && <div className="mt-4">{investorCard}</div>}

      {/* Dashboard impact banner — founder only, hidden for investor-specific evaluations */}
      {!isInvestor && !investorCard && <EvaluationDashboardLink homePath={homePath} />}

      {/* Metric scores + media */}
      {metrics.length > 0 && (
        <div className="mt-5">
          <MetricScoresPanel
            metrics={metrics}
            hideMedia={isPrescreen}
            compact={isInvestor}
            pitchDuration={pitchDuration}
            videoUrl={videoUrl}
            videoModalOpen={videoModalOpen}
            videoResumeTime={videoResumeTime}
            onPlayVideo={onPlayRecording}
            onOpenPitchDeck={onOpenPitchDeck}
          />
        </div>
      )}

      {/* Summary */}
      <EvaluationSummary summary={summary} />

      {/* What changed since last pitch — founder only */}
      {!isInvestor && !isPrescreen && improvements.length > 0 && (
        <EvaluationImprovements improvements={improvements} />
      )}

      {/* Judges grid */}
      {judges.length > 0 && (
        <div className="mt-6 grid grid-cols-2 gap-3">
          {judges.map((judge) => (
            <JudgeCard key={judge.key} judge={judge} detailPath={judgeDetailPath(judge.key)} />
          ))}
          {inlineOpenQuestions && openQuestionsPanel}
        </div>
      )}

      {/* Metric breakdowns (TL;DR — right below judges) */}
      {metricBreakdowns.length > 0 && (
        <div className="mt-6">
          <div className="mb-3 flex items-center gap-3">
            <h2 className="text-sm font-semibold text-foreground">Metric Deep-Dive</h2>
            <span className="text-[10px] text-muted-foreground/80">
              {isPrescreen
                ? 'Strengths · gaps · per metric'
                : 'Per-judge scores · agreements · disagreements'}
            </span>
          </div>
          <div className="space-y-3">
            {metricBreakdowns.map((m) => (
              <MetricBreakdownCard key={m.key} metric={m} />
            ))}
          </div>
        </div>
      )}

      {/* Investment thesis */}
      {winnerJudge && <EvaluationInvestmentThesis winnerJudge={winnerJudge} />}

      {/* Core tensions */}
      {tensions.length > 0 && <EvaluationTensions tensions={tensions} />}

      {/* Risks */}
      {risks.length > 0 && <EvaluationRisks risks={risks} />}

      {/* Open questions — rendered here only when NOT inlined beside a single judge card */}
      {!inlineOpenQuestions && hasOpenQuestions && <div className="mt-8">{openQuestionsPanel}</div>}

      {/* Actionable next steps */}
      {actions.length > 0 && <EvaluationActions actions={actions} />}

      {/* Evaluation feedback */}
      <EvaluationFeedback className="mt-8" onSubmit={onFeedbackSubmit} />

      {/* Analysis depth indicator */}
      <div className="mt-8 flex items-center justify-center gap-3 text-[10px] text-muted-foreground/80">
        <span className="h-px flex-1 bg-border/50" />
        <span className="uppercase tracking-widest">AI-powered review · cross-validated</span>
        <span className="h-px flex-1 bg-border/50" />
      </div>

      {/* Bottom CTA — variant-specific */}
      <section className="mt-6">
        {isInvestor ? (
          <CtaBanner
            icon={<MessageSquare className="size-5 text-[#f97316]" />}
            title="Interested in this startup?"
            description="Reach out to the founder directly"
            actionLabel="Connect with Startup"
            onAction={onConnect}
            variant="primary"
          />
        ) : (
          <CtaBanner
            icon={<Mic className="size-5 text-[#f97316]" />}
            title="Address the feedback and pitch again"
            description="Your dashboard review updates after each session"
            actionLabel="Start AI Session"
            actionTo={pitchPath}
            variant="primary"
          />
        )}
      </section>
    </div>
  )
}
